/**
 * Orders — SLA Heatmap. Stage 7.
 * Sources `/api/bi/orders/sla/heatmap/`.
 */
import { useMemo } from 'react';
import { CalendarRange } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { KPICard } from '@/components/charts/KPICard';
import { useOrdersSlaHeatmap, type SlaHeatmapCell } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const WEEKDAYS = [
  { wd: 1, label: 'Mon' },
  { wd: 2, label: 'Tue' },
  { wd: 3, label: 'Wed' },
  { wd: 4, label: 'Thu' },
  { wd: 5, label: 'Fri' },
  { wd: 6, label: 'Sat' },
  { wd: 7, label: 'Sun' },
];

export function SlaHeatmapPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const query = useOrdersSlaHeatmap({ start: startISO, end: endISO, storeIds });

  const { grid, peakOrders } = useMemo(() => {
    const g = new Map<string, SlaHeatmapCell>();
    let peak = 0;
    for (const c of query.data?.cells ?? []) {
      g.set(`${c.weekday}-${c.hour}`, c);
      peak = Math.max(peak, c.orders);
    }
    return { grid: g, peakOrders: peak };
  }, [query.data?.cells]);

  const totals = query.data?.totals;

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="SLA heatmap"
        description="Where the breaches actually live. Hour × weekday so you can spot the staffing gap, not just the average."
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Orders"
          value={fmt.number(totals?.orders ?? 0)}
          loading={query.isLoading}
          error={query.isError}
          onRetry={() => void query.refetch()}
        />
        <KPICard
          label="Breaches"
          value={fmt.number(totals?.breaches ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
          error={query.isError}
        />
        <KPICard
          label="Breach rate"
          value={fmt.pct(totals?.breach_rate ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
          error={query.isError}
        />
        <KPICard
          label="Peak cell"
          value={fmt.number(totals?.peak_cell_orders ?? 0)}
          loading={query.isLoading}
          error={query.isError}
          hint="Most orders in any single hour×weekday cell."
        />
      </div>

      <Card className="mt-4">
        <CardBody>
          {query.isError ? (
            <ErrorState description="Couldn't load heatmap." onRetry={() => void query.refetch()} />
          ) : query.isLoading ? (
            <div className="h-80 animate-pulse rounded-md bg-muted/40" />
          ) : grid.size === 0 ? (
            // Every cell renders muted when there is no data, so a genuinely
            // empty range looks exactly like a well-behaved week with no
            // breaches — the most flattering possible misreading.
            <EmptyState
              icon={<CalendarRange className="h-5 w-5" />}
              title="No orders in this range"
              description="There is nothing to plot for the selected dates and stores. Try a wider range."
              className="h-80"
            />
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-[40px_repeat(24,minmax(0,1fr))] gap-1 text-[10px] text-muted-foreground">
                  <div />
                  {HOURS.map((h) => (
                    <div key={h} className="text-center font-mono">
                      {h.toString().padStart(2, '0')}
                    </div>
                  ))}
                </div>
                {WEEKDAYS.map((row) => (
                  <div
                    key={row.wd}
                    className="mt-1 grid grid-cols-[40px_repeat(24,minmax(0,1fr))] gap-1"
                  >
                    <div className="flex items-center text-[11px] font-medium text-muted-foreground">
                      {row.label}
                    </div>
                    {HOURS.map((h) => {
                      const cell = grid.get(`${row.wd}-${h}`);
                      return <Cell key={`${row.wd}-${h}`} cell={cell} peak={peakOrders} />;
                    })}
                  </div>
                ))}
                <div className="mt-4 flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span>Volume</span>
                  <div className="flex h-3 w-32 overflow-hidden rounded-full">
                    {[0.05, 0.2, 0.35, 0.5, 0.7, 0.85, 1].map((alpha) => (
                      <div key={alpha} className="flex-1" style={{ background: `hsl(var(--primary) / ${alpha})` }} />
                    ))}
                  </div>
                  <span className="ml-auto">Red ring = breach rate ≥ 25%</span>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function Cell({ cell, peak }: { cell: SlaHeatmapCell | undefined; peak: number }) {
  const orders = cell?.orders ?? 0;
  const breachRate = cell?.breach_rate ?? 0;
  const alpha = peak > 0 && orders > 0 ? Math.max(0.06, Math.min(1, orders / peak)) : 0;
  const breachy = breachRate >= 0.25 && orders > 0;
  return (
    <div
      title={
        cell
          ? `${cell.weekday_label} ${cell.hour
              .toString()
              .padStart(2, '0')}:00 · ${orders} orders, ${cell.breaches} breaches (${(breachRate * 100).toFixed(0)}%)`
          : 'No data'
      }
      className={cn(
        'relative h-7 rounded-sm transition-colors',
        orders === 0 && 'bg-muted/30',
        breachy && 'ring-1 ring-danger',
      )}
      style={
        orders > 0
          ? { background: `hsl(var(--primary) / ${alpha})` }
          : undefined
      }
    />
  );
}
